/**
 * Service Desk API (PHP under public/api → copied to build/api).
 * Dev: point REACT_APP_API_BASE at your XAMPP API, e.g. http://localhost/emicron/build/api
 * (npm start does not run PHP; use XAMPP for API during development.)
 */
export function getApiBase() {
  const env = process.env.REACT_APP_API_BASE;
  if (env && String(env).trim()) {
    return String(env).replace(/\/$/, "");
  }
  const pub = process.env.PUBLIC_URL || "";
  if (pub === "." || pub === "" || pub === "/") {
    return "/api";
  }
  return `${pub.replace(/\/$/, "")}/api`;
}

async function parseJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { ok: false, error: text || "Invalid response" };
  }
}

export async function submitTicket(payload) {
  const isFormData = typeof FormData !== "undefined" && payload instanceof FormData;
  const res = await fetch(`${getApiBase()}/ticket-submit.php`, {
    method: "POST",
    headers: isFormData ? undefined : { "Content-Type": "application/json" },
    body: isFormData ? payload : JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.error || `Submit failed (${res.status})`);
  }
  if (!data.ok) {
    throw new Error(data.error || "Submit failed");
  }
  return data;
}

export async function trackTicket(ticketNumber) {
  const q = encodeURIComponent(ticketNumber.trim());
  const res = await fetch(`${getApiBase()}/ticket-track.php?number=${q}`);
  const data = await parseJson(res);
  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(data.error || `Lookup failed (${res.status})`);
  }
  if (!data.ok || !data.ticket) {
    return null;
  }
  return data.ticket;
}

const TOKEN_KEY = "emicron_admin_token";

export function setAdminToken(token) {
  const clearAll = () => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
    } catch (_) {}
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch (_) {}
  };
  if (!token) {
    clearAll();
    return;
  }
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch (_) {}
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (_) {}
}

function readStoredToken() {
  try {
    const s = sessionStorage.getItem(TOKEN_KEY);
    if (s) return s;
  } catch (_) {}
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (_) {
    return null;
  }
}

export function getAdminToken() {
  return readStoredToken();
}

/** Apache/XAMPP often strips Authorization; X-Admin-Token is read by PHP as fallback. */
function adminAuthHeaders(token) {
  return {
    Authorization: `Bearer ${token}`,
    "X-Admin-Token": token,
  };
}

export async function adminLogin(username, password) {
  const res = await fetch(`${getApiBase()}/admin/login.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password }),
  });
  const data = await parseJson(res);
  if (!res.ok || !data.ok) {
    throw new Error(data.error || "Login failed");
  }
  const tok = typeof data.token === "string" ? data.token.trim() : "";
  if (!tok) {
    throw new Error("Server did not return a token. Check admin/login.php.");
  }
  setAdminToken(tok);
  if (!readStoredToken()) {
    throw new Error("Could not save login (browser storage blocked?). Try another browser or disable strict tracking.");
  }
  return data;
}

export function adminLogout() {
  setAdminToken(null);
}

export async function adminFetchTickets() {
  const token = getAdminToken();
  if (!token) {
    throw new Error("Not logged in");
  }
  const res = await fetch(`${getApiBase()}/admin/tickets.php`, {
    headers: adminAuthHeaders(token),
  });
  const data = await parseJson(res);
  if (res.status === 401) {
    setAdminToken(null);
    throw new Error("Session expired — log in again");
  }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || "Could not load tickets");
  }
  return data.tickets || [];
}

/** Download raw file (admin). Ticket JSON omits large dataUrl — use this for the attachment. */
export async function adminDownloadTicketAttachment(ticketNumber) {
  const token = getAdminToken();
  if (!token) {
    throw new Error("Not logged in");
  }
  const q = encodeURIComponent(ticketNumber.trim());
  const res = await fetch(`${getApiBase()}/admin/ticket-attachment.php?number=${q}`, {
    headers: adminAuthHeaders(token),
  });
  if (res.status === 401) {
    setAdminToken(null);
    throw new Error("Session expired — log in again");
  }
  if (!res.ok) {
    const data = await parseJson(res);
    throw new Error(data.error || `Download failed (${res.status})`);
  }
  const blob = await res.blob();
  let fileName = "attachment";
  const cd = res.headers.get("Content-Disposition");
  if (cd) {
    const m = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"|filename=([^;\s]+)/i.exec(cd);
    const raw = m && (m[1] || m[2] || m[3]);
    if (raw) {
      try {
        fileName = decodeURIComponent(raw.replace(/^"|"$/g, ""));
      } catch (_) {
        fileName = raw.replace(/^"|"$/g, "");
      }
    }
  }
  return { blob, fileName };
}

export async function adminFetchTicketDetail(ticketNumber) {
  const token = getAdminToken();
  if (!token) {
    throw new Error("Not logged in");
  }
  const q = encodeURIComponent(ticketNumber.trim());
  const res = await fetch(`${getApiBase()}/admin/ticket.php?number=${q}`, {
    headers: adminAuthHeaders(token),
  });
  const data = await parseJson(res);
  if (res.status === 401) {
    setAdminToken(null);
    throw new Error("Session expired — log in again");
  }
  if (!res.ok || !data.ok || !data.ticket) {
    throw new Error(data.error || "Could not load ticket");
  }
  return data.ticket;
}

export async function adminUpdateTicket(ticketNumber, status, note) {
  const token = getAdminToken();
  if (!token) {
    throw new Error("Not logged in");
  }
  const res = await fetch(`${getApiBase()}/admin/ticket-update.php`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...adminAuthHeaders(token),
    },
    body: JSON.stringify({ ticketNumber, status, note }),
  });
  const data = await parseJson(res);
  if (res.status === 401) {
    setAdminToken(null);
    throw new Error("Session expired — log in again");
  }
  if (!res.ok || !data.ok) {
    throw new Error(data.error || "Update failed");
  }
  return {
    ticket: data.ticket,
    customerNotified: data.customerNotified === true,
  };
}
